import { CreateDtoInterface, UpdateDtoInterface } from 'booksprout'
import { BsTabDefinition } from '../components/bs-component-bundle/types'
import { BaseModel } from '../modules/_base/base.model'
import { replaceUrlState, UrlStateRouter } from './useUrlFilters'

/**
 * The shaping a create/edit screen does: which mode it is in, what a save payload looks like, what a
 * registered tab's labels are, and the url swap that stops a second create.
 *
 * `save` and `autoSave` are here too, but every step a screen can replace arrives through the context
 * rather than being called on the composable's own copy — the rule stage 7 settled on, and the reason
 * the two wizards' subforms still get their overrides honoured now that the base class is gone.
 *
 * `deleteCrudModel` is `useCrudDelete`, the tab strip is `useTabs`, and leaving-and-coming-back is
 * `useNavigateWithReturn`: each needs a component to hand around — a `parent` for its dialogs, a `data`
 * field to read, or the current route — so each takes one rather than being reachable from here.
 */

export interface TabTracker {
  name: string
  heading: string
  description: string
  hint: string
  actionHint: string
  i18nKey: string
}

export interface TabRegistrar {
  i18nKey: string
  hidden?: boolean
  disable?: boolean
  previousLabel?: string
  nextLabel?: string
  hideNext?: boolean
  isEditMode?: boolean
  stepComplete?: boolean
  previousAction? (): void
  nextAction? (): void
}

/**
 * Everything a save needs that belongs to the caller. `crudModel` is a getter rather than a value
 * because `autoSave` writes the new id back onto it, and the caller may have replaced the object
 * between the call and the response.
 */
export interface SaveContext<TModel extends BaseModel> {
  crudModel (): TModel
  crudService: {
    create (model: CreateDtoInterface): Promise<TModel>
    update (model: UpdateDtoInterface): Promise<TModel>
  }
  doBeforeSave (): Promise<void>
  doAfterSave (doc: TModel): void
  getCreateModel (): CreateDtoInterface
  getUpdateModel (): UpdateDtoInterface
}

export interface AutoSaveContext<TModel extends BaseModel> extends SaveContext<TModel> {
  autoSaveValid (): boolean
  /**
   * The caller's own `save`, not this file's. `save` is itself overridable — the settings screens
   * replace it wholesale to run the update through `authenticationService` (`useSettingsCrud` now), and
   * app-reviewer's arc View replaces it too — so `autoSave` has to dispatch through the caller or those
   * overrides are silently skipped. SitePreferencesDialog.spec caught exactly that.
   */
  save (): Promise<TModel>
  linkService: any
  urlModelName: string
  router: any
}

export interface CrudTranslators {
  /** `$tc` — a plural-aware lookup. */
  tc (key: string): string
  /** `$tcD` — a lookup with a default for keys a module has not translated. */
  tcD (key: string, defaultString: string): string
}

export function useCrud<TModel extends BaseModel> (translators?: CrudTranslators) {
  const t = () => {
    if (translators === void 0) {
      throw new Error('useCrud: tab registration needs $tc and $tcD.')
    }

    return translators
  }

  /**
   * Edit mode is the url, not the model: a create screen that has auto-saved has an id but is still
   * being created. (Removed as part of [ch1157] — the previous rule also treated a saved create as an
   * edit, which it is not.)
   */
  const isEditPath = (path: string) => path.indexOf('/update/') > -1

  /**
   * The default mappings from the screen's model onto the api's create/update dtos. A page overrides
   * these when the shapes diverge; most do not.
   */
  const createModelFrom = (crudModel: TModel): CreateDtoInterface => {
    return { ...crudModel }
  }

  const updateModelFrom = (crudModel: TModel): UpdateDtoInterface => {
    return { ...crudModel, id: crudModel.id || 0 }
  }

  const emptyTabTracker = (): TabTracker => ({
    name: '',
    heading: '',
    description: '',
    hint: '',
    actionHint: '',
    i18nKey: ''
  })

  /**
   * A tab's labels all come from one i18n root, and every one of them is optional — `$tcD` returns '' for
   * a module that has not translated it, which is how a tab shows a heading but no hint.
   */
  const tabTrackerFrom = (i18nRoot: string, tab: TabRegistrar): TabTracker => {
    const { tcD } = t()

    return {
      name: tcD(i18nRoot + '.name', ''),
      heading: tcD(i18nRoot + '.heading', ''),
      description: tcD(i18nRoot + '.description', ''),
      hint: tcD(i18nRoot + '.hint', ''),
      actionHint: tcD(i18nRoot + '.actionHint', ''),
      i18nKey: tab.i18nKey
    }
  }

  const tabDefinitionFrom = (index: number, i18nRoot: string, tab: TabRegistrar) => {
    const { tc, tcD } = t()

    return {
      ...new BsTabDefinition(index, tc(i18nRoot + '.name')),
      ...tab,
      actionHint: tcD(i18nRoot + '.actionHint', ''),
      slotKey: tab.i18nKey
    }
  }

  const tabI18nRoot = (rootI18nModuleKey: string, tab: TabRegistrar) => {
    return `modules.${rootI18nModuleKey}.formFields.tabs.${tab.i18nKey}`
  }

  /**
   * After an auto-save on a create screen, swap the url to /update/:id so a refresh, a back/forward, or
   * a bfcache restore does not land on /create with a fresh model and trigger a second CREATE.
   *
   * Through the router's history (`replaceUrlState`) rather than `router.replace`, so the component is
   * not re-mounted. And not with the browser's `history.replaceState` either: vue-router 4 put `/create`
   * back on the author's next click, so Back from wherever it led opened a blank form — the very path
   * this swap is here to close (sc-28993). The router's history adds Quasar's publicPath itself, so the
   * url goes in app-relative.
   */
  const swapCreateUrlForUpdate = (context: {
    linkService?: { adminUpdate (model: string, id: number): string }
    urlModelName?: string
    router?: UrlStateRouter
    id: number
  }) => {
    if (!context.linkService || !context.urlModelName || !context.router) {
      return
    }

    const appRelative = context.linkService.adminUpdate(context.urlModelName, context.id)
    /**
     * The hash rides along. This swap is about the PATH — /create becomes /update/:id — and the hash is
     * the wizard's step, written separately (`pushUrlHash`, from BsTabStepper and the forms). Dropping it
     * here left the url describing no step at all until the next step change put one back, and any code
     * that reads "is there a hash?" to decide where to open then saw the wrong answer (sc-27874).
     */
    replaceUrlState(context.router, appRelative + window.location.hash)
  }

  /**
   * One save: create when the model has no id, update when it has one.
   *
   * Every replaceable step is taken from the context — `doBeforeSave` runs first, the payload comes
   * from whichever mapper the caller supplied, and `doAfterSave` sees the server's answer. Nothing
   * here reaches back into the caller for them.
   */
  const save = async (context: SaveContext<TModel>): Promise<TModel> => {
    await context.doBeforeSave()

    const model = context.crudModel()

    if (model.id === void 0) {
      const newModel = await context.crudService.create(context.getCreateModel())
      context.doAfterSave(newModel)

      return newModel
    }

    const updatedModel = await context.crudService.update(context.getUpdateModel())
    context.doAfterSave(updatedModel)

    return updatedModel
  }

  /**
   * The save a field blur triggers. Three things distinguish it from `save`:
   *
   *  - it is a no-op when the caller's `autoSaveValid` says the model is not ready;
   *  - after the first create it writes the new id back onto the caller's model, so the next auto save
   *    is an update rather than a second create;
   *  - and it swaps the create url for the edit one, so a refresh does not start over.
   *
   * The rejection is deliberately re-thrown: the form binds it to the offending field.
   */
  const autoSave = async (context: AutoSaveContext<TModel>): Promise<TModel | any> => {
    if (!context.autoSaveValid()) {
      return Promise.resolve(void 0)
    }

    return context.save().then(r => {
      const model = context.crudModel()

      if (model.id === void 0) {
        model.id = r.id
        model.createdDate = r.createdDate

        swapCreateUrlForUpdate({
          linkService: context.linkService,
          urlModelName: context.urlModelName,
          router: context.router,
          id: r.id
        })
      }
    }).catch(e => {
      // Rejecting here sends the error down to the auto save and in turn shows it against the field.
      return Promise.reject(e)
    })
  }

  return {
    isEditPath,
    createModelFrom,
    updateModelFrom,
    emptyTabTracker,
    tabTrackerFrom,
    tabDefinitionFrom,
    tabI18nRoot,
    swapCreateUrlForUpdate,
    save,
    autoSave
  }
}
